import { EmptyObject } from '@common/utils'

import { FeedbackType } from '@plco-pro/graphqls/react.generated'

type PrependNextNum<A extends Array<unknown>> = A['length'] extends infer T
  ? ((t: T, ...a: A) => void) extends (...x: infer X) => void
    ? X
    : never
  : never
type EnumerateInternal<A extends Array<unknown>, N extends number> = {
  0: A
  1: EnumerateInternal<PrependNextNum<A>, N>
}[N extends A['length'] ? 0 : 1]

type Enumerate<N extends number> = EnumerateInternal<[], N> extends (infer E)[] ? E : never
// ex: RANGE<1, 5> = 1 | 2 | 3| 4
export type Range<FROM extends number, TO extends number> = Exclude<Enumerate<TO>, Enumerate<FROM>>

export type AnalyticsPattern = {
  pattern: string
  event: PageEventName
  track?: boolean
}

export enum AnalyticsEventType {
  // account
  ACCOUNT_CREATION = 'account_creation',

  // payment
  PAYMENT_PLAN_MONTH_SELECTED = 'payment_plan_month_selected',
  PAYMENT_PLAN_YEAR_SELECTED = 'payment_plan_year_selected',
  PAYMENT_PLAN_INQUIRY_CLICKED = 'payment_plan_inquiry_clicked',
  PAYMENT_SUBSCRIPTION_COMPLETED = 'payment_subscription_completed',
  PAYMENT_SUBSCRIPTION_CANCEL = 'payment_subscription_cancel',
  PAYMENT_SUBSCRIPTION_REFUND = 'payment_subscription_refund',

  // squad
  STARRED_ADDITION = 'starred_addition',
  STARRED_CANCELLATION = 'starred_cancellation',
  CONDITION_CLICKED = 'condition_clicked',
  WORKLOAD_CLICKED = 'workload_clicked',
  ACWR_CLICKED = 'acwr_clicked',
  BODY_CLICKED = 'body_clicked',
  INJURY_CLICKED = 'injury_clicked',
  REGISTER_INJURY = 'register_injury',
  DELETE_INJURY = 'delete_injury',
  PLAYER_PAGE_CLICKED = 'player_page_clicked',

  // feedback
  REGISTER_FEEDBACK_CLICKED = 'register_feedback_clicked',
  REGISTER_FEEDBACK = 'register_feedback',
  MODIFY_FEEDBACK = 'modify_feedback',
  DELETE_FEEDBACK = 'delete_feedback',
  VIEW_FEEDBACK = 'view_feedback',
  SAVE_FEEDBACK_COMMENT = 'save_feedback_comment',
  DELETE_FEEDBACK_COMMENT = 'delete_feedback_comment',

  // payment
  PAYMENT_COMPLETED = 'payment_completed',
}
export type ConditionDataType =
  | 'mood'
  | 'fitness'
  | 'sleep duration'
  | 'sleep quality'
  | 'condition'
  | 'fatigue'
  | 'stress'
export type WorkloadDataType =
  | 'workload'
  | 'workload duration'
  | 'workload satisfaction'
  | 'workload intensity'
export type AcwrDataType = 'acwr' | 'acute & chronic workload' | 'monotony'
export type InjuryDataType =
  | 'soreness 1'
  | 'soreness 2'
  | 'soreness 3'
  | 'soreness 4'
  | 'soreness 5'

export type WorkloadTableDataType = 'workload' | 'intensity' | 'duration' | 'satisfaction'

export type DataType = ConditionDataType | WorkloadDataType | AcwrDataType | InjuryDataType
export type TableColumnType = 'name' | Exclude<DataType, InjuryDataType>
export type DateType = 'date picker' | 'arrow' | 'today'
export const CATEGORY_TYPE: Record<string, DataType> = {
  injury_3: 'soreness 3',
  injury_4: 'soreness 4',
  injury_5: 'soreness 5',
  workload_acxr: 'acwr',
  workload_ax_cx: 'acute & chronic workload',
  workload_axd: 'monotony',
}

export enum InjuryType {
  current = 'current',
  past = 'past',
}

export type ExcludedFeedbackType =
  | Exclude<FeedbackType, FeedbackType.WORKLOAD_MEMO>
  | Exclude<FeedbackType, FeedbackType.WORKLOAD_MEMO | FeedbackType.REQUEST>
export const EXCLUDED_FEEDBACK_TYPE: Record<ExcludedFeedbackType, string> = {
  NORMAL: 'normal',
  CONDITION_NOT_GOOD: 'bad_condition',
  CONDITION_PLUMMET: 'plummeted_condition',
  WORKLOAD_RISK_AT_INJURY: 'injury_risk',
  SORENESS_SEVERE: 'serious_soreness',
  REQUEST: 'feedback_request',
}

export type AnalyticsEventParams = {
  // account
  [AnalyticsEventType.ACCOUNT_CREATION]: EmptyObject

  // payment
  [AnalyticsEventType.PAYMENT_PLAN_MONTH_SELECTED]: {
    plan: string
  }
  [AnalyticsEventType.PAYMENT_PLAN_YEAR_SELECTED]: {
    plan: string
  }
  [AnalyticsEventType.PAYMENT_PLAN_INQUIRY_CLICKED]: EmptyObject
  [AnalyticsEventType.PAYMENT_SUBSCRIPTION_COMPLETED]: {
    plan: string
  }
  [AnalyticsEventType.PAYMENT_SUBSCRIPTION_CANCEL]: {
    plan: string
  }

  // squad
  [AnalyticsEventType.STARRED_ADDITION]: EmptyObject
  [AnalyticsEventType.STARRED_CANCELLATION]: EmptyObject
  [AnalyticsEventType.CONDITION_CLICKED]: EmptyObject
  [AnalyticsEventType.WORKLOAD_CLICKED]: EmptyObject
  [AnalyticsEventType.ACWR_CLICKED]: EmptyObject
  [AnalyticsEventType.BODY_CLICKED]: EmptyObject
  [AnalyticsEventType.INJURY_CLICKED]: EmptyObject
  [AnalyticsEventType.REGISTER_INJURY]: {
    injury_type: InjuryType
    file_attached: boolean
    conversion: string
  }
  [AnalyticsEventType.DELETE_INJURY]: {
    injury_type: InjuryType
  }
  [AnalyticsEventType.PLAYER_PAGE_CLICKED]: EmptyObject

  // feedback
  [AnalyticsEventType.REGISTER_FEEDBACK_CLICKED]: {
    conversion: string
  }
  [AnalyticsEventType.REGISTER_FEEDBACK]: {
    type: string
    conversion: string
  }
  [AnalyticsEventType.MODIFY_FEEDBACK]: {
    type: string
  }
  [AnalyticsEventType.DELETE_FEEDBACK]: {
    type: string
  }
  [AnalyticsEventType.VIEW_FEEDBACK]: {
    type: string
    conversion: string
  }
  [AnalyticsEventType.SAVE_FEEDBACK_COMMENT]: {
    type: string
  }
  [AnalyticsEventType.DELETE_FEEDBACK_COMMENT]: {
    type: string
  }
}

export enum PageEventName {
  BLOG_CONTENT = 'pv_blog_contents',
  PLAN_SELECT = 'pv_plan_select',
  PAYMENT_MANAGEMENT = 'pv_payment_management',
  PLAYERS = 'pv_players',
}
